/**
 * NotificationPermissionCard — Carte in-app "amie" qui explique à quoi servent
 * les notifications MamaLink AVANT de déclencher le prompt navigateur.
 *
 * 🎯 Règles produit (Élodie) :
 *  - S'affiche après une connexion/reprise de session (jamais automatiquement au
 *    chargement d'une page publique).
 *  - N'appelle JAMAIS `Notification.requestPermission()` toute seule : le prompt
 *    natif ne se déclenche QUE sur clic explicite sur "Activer les notifications".
 *  - Bouton "Plus tard" : la carte se remet en veille 24h (localStorage), puis
 *    ré-apparaîtra à la prochaine session/après snooze.
 *  - Ne s'affiche PAS si la permission est déjà "granted" ou "denied" (dans ce
 *    dernier cas, on ne peut plus rien via JS, la maman doit passer par les
 *    réglages système — géré via ProfileSettingsPage).
 *  - Ne s'affiche pas dans l'app native (le canal push natif iOS/Android est
 *    géré indépendamment côté shell).
 */
import React, { useEffect, useState } from 'react';
import { Bell, MessageCircle, Heart, Clock, Shield, X } from 'lucide-react';
import { toast } from 'sonner';
import { subscribeToNotifications } from '../utils/pushNotifications';

const SNOOZE_KEY = 'mamalink_notif_prompt_snoozed_until';
const SNOOZE_MS = 24 * 60 * 60 * 1000; // 24h

const DEFAULT_NOTIF_PREFS = {
  enabled: true,
  new_message: true,
  new_match: true,
  new_forum_reply: true,
  hydration_reminder: true,
  emotional_barometer: true,
  question_reminder: true,
};

const isSnoozed = () => {
  try {
    const until = parseInt(localStorage.getItem(SNOOZE_KEY) || '0', 10);
    return until > Date.now();
  } catch {
    return false;
  }
};

const snooze = () => {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
  } catch { /* localStorage indisponible → on laisse passer */ }
};

const shouldShow = () => {
  if (typeof window === 'undefined') return false;
  // 📱 App native : canal push géré côté shell, pas via Web Push
  if (window.isNativeApp) return false;
  if (!('Notification' in window)) return false;
  // Permission déjà accordée ou explicitement refusée → on ne montre plus
  if (Notification.permission !== 'default') return false;
  if (isSnoozed()) return false;
  return true;
};

const NotificationPermissionCard = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    // Petit délai pour laisser le dashboard se poser avant d'afficher la carte
    const timer = setTimeout(() => {
      if (shouldShow()) setOpen(true);
    }, 1200);
    return () => clearTimeout(timer);
  }, [token]);

  if (!open) return null;

  const handleActivate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // subscribeToNotifications (non-silent) déclenche Notification.requestPermission()
      // ➜ enregistre le SW ➜ POST /api/notifications/subscribe côté backend.
      const ok = await subscribeToNotifications(token, DEFAULT_NOTIF_PREFS);
      if (ok) {
        toast.success('Notifications activées 💕 Tu ne rateras plus rien !');
      }
      // Si la maman a refusé le prompt navigateur, subscribeToNotifications a
      // déjà affiché son propre toast. Dans tous les cas on ferme la carte —
      // on ne la ré-affichera pas tant que la permission n'est plus "default".
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  const handleLater = () => {
    snooze();
    setOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center p-4"
      onClick={handleLater}
      data-testid="notif-permission-card"
      role="dialog"
      aria-modal="true"
      aria-labelledby="notif-permission-title"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl relative animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleLater}
          className="absolute top-3 right-3 p-1.5 rounded-full text-[#9D8189] hover:bg-[#F8EDEB]"
          data-testid="notif-permission-close"
          aria-label="Fermer"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#FFB5A7] to-[#FF6B9D] flex items-center justify-center mb-4 shadow-md">
          <Bell className="w-8 h-8 text-white" />
        </div>

        <h3
          id="notif-permission-title"
          className="text-lg font-bold text-[#5C3D36] font-serif text-center mb-2"
        >
          Reste connectée à ta communauté 💕
        </h3>
        <p className="text-sm text-[#9D8189] text-center leading-relaxed mb-4">
          Active les notifications MamaLink pour recevoir en douceur ce qui
          compte vraiment pour toi.
        </p>

        <ul className="space-y-2.5 mb-5" data-testid="notif-permission-reasons">
          <li className="flex items-start gap-3 bg-[#FFF5F3] rounded-xl px-3 py-2.5">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#FFB5A7] to-[#FCD5CE] flex items-center justify-center flex-shrink-0">
              <Clock className="w-4 h-4 text-white" />
            </span>
            <div>
              <p className="text-sm font-semibold text-[#5C3D36] leading-tight">
                Rappels bienveillants
              </p>
              <p className="text-xs text-[#9D8189] leading-snug">
                Hydratation, humeur du soir, rendez-vous bébé — au bon moment,
                sans jamais te déranger.
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3 bg-[#F5EEFF] rounded-xl px-3 py-2.5">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#CDB4DB] to-[#E2D1F9] flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-4 h-4 text-white" />
            </span>
            <div>
              <p className="text-sm font-semibold text-[#5C3D36] leading-tight">
                Réponses à tes posts
              </p>
              <p className="text-xs text-[#9D8189] leading-snug">
                Quand une maman répond à ta question ou commente ton
                témoignage, tu le sais tout de suite.
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3 bg-[#FFF0F5] rounded-xl px-3 py-2.5">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#FFC8DD] to-[#FFDEEB] flex items-center justify-center flex-shrink-0">
              <Heart className="w-4 h-4 text-white" />
            </span>
            <div>
              <p className="text-sm font-semibold text-[#5C3D36] leading-tight">
                Messages de ta binôme
              </p>
              <p className="text-xs text-[#9D8189] leading-snug">
                Ne rate jamais un mot doux ou un coup de fil de ton match.
              </p>
            </div>
          </li>
        </ul>

        <div className="bg-[#FFF1E6] rounded-xl p-3 mb-5 flex items-start gap-2">
          <Shield className="w-4 h-4 text-[#FFB5A7] mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-[#5C3D36] leading-snug">
            Tu peux tout personnaliser (ou tout couper) plus tard dans
            <strong> Profil &gt; Notifications</strong>. Aucune pub, jamais.
          </p>
        </div>

        <button
          onClick={handleActivate}
          disabled={busy}
          className="w-full py-3 px-4 bg-gradient-to-r from-[#FFB5A7] to-[#FF6B9D] hover:from-[#FFC5BA] hover:to-[#FF7BAD] disabled:opacity-60 text-white font-semibold rounded-xl shadow-sm hover:shadow-md transition-all"
          data-testid="notif-permission-activate"
        >
          {busy ? 'Activation…' : 'Activer les notifications'}
        </button>
        <button
          onClick={handleLater}
          disabled={busy}
          className="w-full mt-2 py-2.5 text-sm text-[#9D8189] hover:text-[#5C3D36] font-medium disabled:opacity-60"
          data-testid="notif-permission-later"
        >
          Plus tard
        </button>
      </div>
    </div>
  );
};

export default NotificationPermissionCard;
